import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { z } from "zod";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import { SettingsLayout } from "./components/layout/SettingsLayout";
import { SolutionLayout } from "./components/layout/SolutionLayout";
import { authClient } from "./lib/auth-client";
import { requireAdmin, requireAuth } from "./lib/route-guards";
import { RootLayout } from "./routes/__root";
import { AcceptPage } from "./routes/accept";
import { DashboardPage } from "./routes/dashboard/index";
import { ItemsPage } from "./routes/dashboard/items";
import { ItemDetailPage } from "./routes/dashboard/items/detail";
import { SettingsSystemPage } from "./routes/dashboard/settings/system";
import { SettingsUsersPage } from "./routes/dashboard/settings/users";
import { LoginPage } from "./routes/login";
import { SharePage } from "./routes/r/$token";
import { ConflictsPage } from "./routes/s/$solutionId/conflicts";
import { FollowupsPage } from "./routes/s/$solutionId/followups";
import { IntakePage } from "./routes/s/$solutionId/intake";
import { MembersPage } from "./routes/s/$solutionId/members";
import { QuestionsPage } from "./routes/s/$solutionId/questions";
import { ReportPage } from "./routes/s/$solutionId/report";
import { ReviewItemPage, ReviewPage } from "./routes/s/$solutionId/review";
import { ScopePage, ScopeStoryPage } from "./routes/s/$solutionId/scope";
import { SignupPage } from "./routes/signup";
import { SolutionsListPage } from "./routes/solutions/index";

const rootRoute = createRootRoute({ component: RootLayout });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: async () => {
    const session = await authClient.getSession();
    throw redirect({ to: session.data ? "/solutions" : "/login" });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
  validateSearch: z.object({
    invite: z.string().optional().catch(undefined),
  }),
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: SignupPage,
  validateSearch: z.object({
    invite: z.string().optional().catch(undefined),
    email: z.string().optional().catch(undefined),
  }),
});

const acceptRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/accept",
  component: AcceptPage,
  validateSearch: z.object({
    token: z.string().optional().catch(undefined),
  }),
});

// /solutions — landing page (auth-guarded; no per-solution shell)

const solutionsListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/solutions",
  beforeLoad: requireAuth,
  component: SolutionsListPage,
});

// /s/$solutionId/* — solution shell with sidebar + topbar

const solutionSearchSchema = z.object({
  stage: z.enum(["sales", "discovery", "build"]).default("discovery").catch("discovery"),
});

const solutionLayout = createRoute({
  getParentRoute: () => rootRoute,
  path: "/s/$solutionId",
  beforeLoad: requireAuth,
  component: SolutionLayout,
  validateSearch: solutionSearchSchema,
});

const solutionIndexRoute = createRoute({
  getParentRoute: () => solutionLayout,
  path: "/",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/s/$solutionId/intake", params });
  },
});

const intakeRoute = createRoute({
  getParentRoute: () => solutionLayout,
  path: "intake",
  component: IntakePage,
});

const reviewSearchSchema = z.object({
  status: z
    .enum(["pending", "in-scope", "in-review", "out-of-scope", "deferred"])
    .optional()
    .catch(undefined),
  type: z
    .enum([
      "feature",
      "implied",
      "tech-req",
      "tech-cons",
      "business",
      "integration",
      "ui",
      "copy",
      "process",
      "nfr",
      "passing",
    ])
    .optional()
    .catch(undefined),
  speakerId: z.string().optional().catch(undefined),
  search: z.string().optional().catch(undefined),
  view: z.enum(["row", "tile"]).default("row").catch("row"),
});

const reviewRoute = createRoute({
  getParentRoute: () => solutionLayout,
  path: "review",
  component: ReviewPage,
  validateSearch: reviewSearchSchema,
});

const reviewItemRoute = createRoute({
  getParentRoute: () => reviewRoute,
  path: "$itemId",
  component: ReviewItemPage,
});

const followupsSearchSchema = z.object({
  view: z.enum(["row", "tile"]).default("row").catch("row"),
});

const followupsRoute = createRoute({
  getParentRoute: () => solutionLayout,
  path: "followups",
  component: FollowupsPage,
  validateSearch: followupsSearchSchema,
});

const scopeSearchSchema = z.object({
  view: z.enum(["list", "tile"]).default("list").catch("list"),
  /** When in tile view, the drilled-in deliverable id. */
  d: z.string().optional().catch(undefined),
  /** When in tile view, the drilled-in epic id (requires d). */
  e: z.string().optional().catch(undefined),
});

const scopeRoute = createRoute({
  getParentRoute: () => solutionLayout,
  path: "scope",
  component: ScopePage,
  validateSearch: scopeSearchSchema,
});

const scopeStoryRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "$storyId",
  component: ScopeStoryPage,
});

const questionsSearchSchema = z.object({
  /** Open this question's detail sheet on load. */
  q: z.string().optional().catch(undefined),
});

const questionsRoute = createRoute({
  getParentRoute: () => solutionLayout,
  path: "questions",
  component: QuestionsPage,
  validateSearch: questionsSearchSchema,
});

const conflictsRoute = createRoute({
  getParentRoute: () => solutionLayout,
  path: "conflicts",
  component: ConflictsPage,
});

const reportSearchSchema = z.object({
  view: z.enum(["dashboard", "sheet"]).default("dashboard").catch("dashboard"),
  /** Comma-separated column IDs in display order. Hidden cols are absent. */
  cols: z.string().optional().catch(undefined),
  /** Column ID to sort by. */
  sort: z.string().optional().catch(undefined),
  /** Sort direction. */
  dir: z.enum(["asc", "desc"]).optional().catch(undefined),
  /** Custom column widths. Format: id:80,source:120 */
  widths: z.string().optional().catch(undefined),
});

const reportRoute = createRoute({
  getParentRoute: () => solutionLayout,
  path: "report",
  component: ReportPage,
  validateSearch: reportSearchSchema,
});

const membersRoute = createRoute({
  getParentRoute: () => solutionLayout,
  path: "members",
  component: MembersPage,
});

// /dashboard/* — legacy template scaffolding kept until the scope-poc views replace it.

function DashboardLayoutRoute() {
  return (
    <DashboardLayout>
      <Outlet />
    </DashboardLayout>
  );
}

const dashboardLayout = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  beforeLoad: requireAuth,
  component: DashboardLayoutRoute,
});

const dashboardIndex = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/",
  component: DashboardPage,
});

const itemsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/items",
  component: ItemsPage,
});

const itemDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/items/$itemId",
  component: ItemDetailPage,
});

function SettingsLayoutRoute() {
  return (
    <SettingsLayout>
      <Outlet />
    </SettingsLayout>
  );
}

const settingsLayout = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/settings",
  beforeLoad: async (ctx) => {
    await requireAdmin(ctx);
    if (ctx.location.pathname === "/dashboard/settings") {
      throw redirect({ to: "/dashboard/settings/users" });
    }
  },
  component: SettingsLayoutRoute,
});

const settingsUsersRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/users",
  component: SettingsUsersPage,
});

const settingsSystemRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/system",
  component: SettingsSystemPage,
});

/** Public read-only share view. No auth — token-scoped. */
const shareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/r/$token",
  component: SharePage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  signupRoute,
  acceptRoute,
  shareRoute,
  solutionsListRoute,
  solutionLayout.addChildren([
    solutionIndexRoute,
    intakeRoute,
    reviewRoute.addChildren([reviewItemRoute]),
    followupsRoute,
    scopeRoute.addChildren([scopeStoryRoute]),
    questionsRoute,
    conflictsRoute,
    reportRoute,
    membersRoute,
  ]),
  dashboardLayout.addChildren([
    dashboardIndex,
    itemsRoute,
    itemDetailRoute,
    settingsLayout.addChildren([settingsUsersRoute, settingsSystemRoute]),
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
